import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { Stack } from '@mantine/core';
import debounce from 'debounce-promise';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  AsyncDisplay,
  Button,
  CardV2 as Card,
  Icon,
  MonthSwitcher,
  showError,
  Skeleton,
  Table,
  Toolbar,
} from '@feathr/components';
import { type MonthName, MONTHS } from '@feathr/components/dist/MonthSwitcher';
import { useStore } from '@feathr/extender/state';
import type { IPagination } from '@feathr/rachis/src/collection';

import { campaignBreakdownColumns } from './CampaignBreakdownColumns';
import type { ITransformedCampaignData } from './EmailLimits.types';
import EmailLimitsExportModal from './EmailLimitsExportModal';
import { transformCampaignData } from './EmailLimitsPage.transformers';

function CampaignBreakdownCard(): JSX.Element {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pagination, setPagination] = useState<IPagination>({
    page: 0,
    page_size: 100,
  });
  const [campaignData, setCampaignData] = useState<ITransformedCampaignData[]>([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const { EmailUsageRecords } = useStore();

  const debouncedMonthChange = useMemo(
    () =>
      debounce((date: Date, pageSize: number) => {
        setSelectedDate(date);
        setPagination({ page: 0, page_size: pageSize });
      }, 500),
    [],
  );

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const campaignData = await EmailUsageRecords.getCampaignBreakdown({
          page: pagination,
          target_date: selectedDate.toISOString(),
        });

        const transformedCampaignData = campaignData.data.map((campaignRecord) =>
          transformCampaignData(campaignRecord),
        );
        setCampaignData(transformedCampaignData);
        setTotalPages(campaignData?.meta?.page?.pages ?? 0);
      } catch (error) {
        showError(error, t);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [pagination, selectedDate]);

  const handleMonthChange = (monthName: MonthName, year: number): void => {
    // Convert month name to number and format as YYYY-MM
    const monthNumber = MONTHS.indexOf(monthName);

    setCampaignData([]);
    setTotalPages(0);

    // Use debounced function to set both date and pagination to avoid multiple API calls
    debouncedMonthChange(new Date(year, monthNumber, 1), pagination.page_size ?? 100);
  };

  const handleExport = (): void => {
    setIsExportModalOpen(true);
  };

  function onPageSizeChange(newPageSize: number): void {
    // Reset to first page when page size changes
    setPagination({ page: 0, page_size: newPageSize });
  }

  function onPageChange(newPage: number): void {
    setPagination({ page: newPage, page_size: pagination.page_size });
  }

  // Calculate total emails sent for filtered data
  const totalEmailsSent = campaignData.reduce((sum, campaign) => sum + campaign.total, 0);

  // Get campaign columns with calculated total
  const campaignColumns = campaignBreakdownColumns(totalEmailsSent);

  // Create table skeleton
  const tableSkeleton = (
    <Stack gap={8}>
      <Skeleton height={52} width={'100%'} />
      <Skeleton height={44} width={'100%'} />
      <Skeleton height={44} width={'100%'} />
      <Skeleton height={44} width={'100%'} />
      <Skeleton height={44} width={'100%'} />
    </Stack>
  );

  // We only have data for Oct 2025 and later
  const minDate = new Date('2025-10-01').toISOString();

  return (
    <Card width={'full'}>
      <Card.Header title={'Campaign Breakdown'}>
        <Toolbar>
          <MonthSwitcher
            allowFuture={false}
            height={'var(--button-with-icon-size)'}
            minDate={minDate}
            onChanged={handleMonthChange}
          />
          <Button onClick={handleExport} prefix={<Icon icon={faDownload} />} type={'icon-outlined'}>
            {t('Export')}
          </Button>
        </Toolbar>
      </Card.Header>
      <Card.Content>
        <AsyncDisplay isLoading={isLoading} skeleton={tableSkeleton}>
          <Table
            columns={campaignColumns}
            idKey={'id'}
            items={campaignData}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            page={pagination.page}
            pages={totalPages}
            pageSize={pagination.page_size}
          />
        </AsyncDisplay>
      </Card.Content>
      <EmailLimitsExportModal
        exportType={'campaign'}
        onClose={(): void => setIsExportModalOpen(false)}
        opened={isExportModalOpen}
        targetDate={selectedDate}
      />
    </Card>
  );
}

export default observer(CampaignBreakdownCard);
